// =====================================================
// MODAL: AddNoteModal
// Description: Modal simple pour ajouter une note
// =====================================================

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal, ModalFooter } from '../ui/modal';
import { Form } from '../ui/form';
import { FormTextarea } from '../ui';
import type { EntityType } from '@/types/timeline';
import { useSupabase } from '@/hooks/useSupabase';
import { getEntityDisplayName } from '@/lib/timeline-logger';
import { toast } from 'sonner';

interface AddNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: EntityType;
  entityId: string;
  onSuccess?: () => void;
}

const addNoteSchema = z.object({
  note: z.string().min(1, 'Note requise'),
});

type AddNoteInput = z.infer<typeof addNoteSchema>;

export function AddNoteModal({
  isOpen,
  onClose,
  entityType,
  entityId,
  onSuccess,
}: AddNoteModalProps) {
  const supabase = useSupabase();
  const form = useForm<AddNoteInput>({
    resolver: zodResolver(addNoteSchema),
    defaultValues: {
      note: '',
    },
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = async (data: AddNoteInput) => {
    try {
      // ✅ Récupérer le nom de l'entité pour un contexte clair
      const entityName = await getEntityDisplayName(supabase, entityType, entityId);
      const titlePrefix = entityName ? `${entityName} - ` : '';

      await supabase.from('timeline_events').insert({
        entity_type: entityType,
        entity_id: entityId,
        event_type: 'note_created',
        title: `${titlePrefix}Note ajoutée`,
        description: data.note,
        metadata: {
          note: data.note,
          entity_name: entityName,
        },
      });

      toast.success('Note ajoutée', {
        description: "La note a été ajoutée à l'historique",
      });

      if (onSuccess) {
        onSuccess();
      }

      handleClose();
    } catch (error) {
      console.error("Erreur lors de l'ajout de la note:", error);
      toast.error('Erreur', {
        description: "Impossible d'ajouter la note",
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Ajouter une note"
      size="lg"
      footer={
        <ModalFooter
          onCancel={handleClose}
          submitLabel="Ajouter"
          isSubmitting={form.formState.isSubmitting}
          formId="add-note-form"
        />
      }
    >
      <Form {...form}>
        <form id="add-note-form" onSubmit={form.handleSubmit(handleSubmit)}>
          <FormTextarea
            control={form.control as any}
            name="note"
            label="Note"
            placeholder="Ajoutez votre note..."
            rows={6}
            required
          />
        </form>
      </Form>
    </Modal>
  );
}
